import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { Backdrop, Button, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCropsStore, useMonitoringCrops } from 'services/crops';
import { useLanguages, useLanguagesStore } from 'services/languages';
import { useMonitoringStore } from 'services/monitoring';
import { submitInitialStep } from 'services/monitoring/monitoringService';
import { useGetStoredData } from 'services/monitoring/monitoringHooks';
import { useGetUserByCropsAndLanguages } from 'services/users/usersHook';
import * as Yup from 'yup';
import { AutocompleteField } from '@features/FindSpecialist/components/AutocompleteField';
import { CropAreaAndUnit } from '@features/FindSpecialist/components/CropAndUnit';
import { LocationInput } from '@features/FindSpecialist/components/LocationInput';
import { RoleSelector } from '@features/FindSpecialist/components/RoleSelector';
import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { useGeolocated } from '@components/shared/Maps/useGeoLocated';
import { useCurrentAddress } from '@components/shared/Maps/utils';
import { useModalDimensions } from '@hooks/useModalDimensions';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import { PackagesFormValues, validationSchema } from './utils';

const rolesData = [
	{ role: 'Standard', titleKey: 'findSpecialistPage.monitoring.standard', isDisabled: false },
	{ role: 'Premium', titleKey: 'findSpecialistPage.monitoring.premium', isDisabled: true },
];

const backdropStyle = {
	color: '#fff',
	zIndex: (theme: any) => theme.zIndex.drawer + 1 + 1000,
	display: 'flex',
	flexDirection: 'column',
	gap: 2,
};

// Add this wrapper component to ensure Autocomplete values are always properly defined
const SafeAutocompleteField = (props: any) => {
	// Ensure value is never undefined
	const safeProps = {
		...props,
		formik: {
			...props.formik,
			values: {
				...props.formik.values,
				[props.name]: props.formik.values[props.name] === undefined ? null : props.formik.values[props.name]
			}
		}
	};
	
	return <AutocompleteField {...safeProps} />;
};

// Add a safe wrapper for CropAreaAndUnit
const SafeCropAreaAndUnit = (props: any) => {
	// Create a safe version of formik with guaranteed non-undefined values
	const safeFormik = {
		...props.formik,
		values: {
			...props.formik.values,
			crop_unit: props.formik.values.crop_unit === undefined ? null : props.formik.values.crop_unit
		}
	};
	
	return <CropAreaAndUnit {...props} formik={safeFormik} />;
};

const PackagesStepComponent = ({ customSubmitHandler }: { customSubmitHandler?: (values: PackagesFormValues) => Promise<void> }) => {
	const [mapOpen, setMapOpen] = useState(false);
	const [submitIsLoading, setSubmitIsLoading] = useState(false);
	const [isNavigating, setIsNavigating] = useState(false);
	const previousValuesRef = useRef<string | null>(null);
	const [isNavigatingFromTerms, setIsNavigatingFromTerms] = useState(false);

	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const { initialStepData, setInitialStepData, stepsDraft, setStepsDraft } = useMonitoringStore();
	const { cropsMonitoring } = useCropsStore();
	const { languages } = useLanguagesStore();
	const modalDimensions = useModalDimensions();
	const { coords } = useGeolocated();
	const { address, loading } = useCurrentAddress();

	const cropUnitOptions: any = [
		{ name: t('unitTypes.m2'), id: 'm2' },
		{ name: t('unitTypes.hectares'), id: 'hectares' },
		{ name: t('unitTypes.acres'), id: 'acres' },
	];

	const position = {
		lat: coords?.latitude || 0,
		lng: coords?.longitude || 0,
	};

	useMonitoringCrops();
	useLanguages();

	// Load saved form data if available
	const savedFormData = localStorage.getItem('monitoringPackagesFormData');
	const initialStepDataId = localStorage.getItem('initialStepDataId');
	
	// Only call useGetStoredData if we have an ID and no saved form data
	// This prevents API calls from overwriting user data
	const { data: storedData } = useGetStoredData(initialStepDataId && !savedFormData ? initialStepDataId : null);

	// Initialize form with saved data or defaults
	let initialFormValues: PackagesFormValues = {
		crop: null,
		latitude: position.lat,
		longitude: position.lng,
		crop_location_name: address || '',
		communication_language: [],
		crop_area: '',
		package_type: 'Standard' as 'Standard' | 'Premium' | null,
		crop_unit: { name: t('unitTypes.hectares'), id: 'hectares' },
	};

	// Try to load saved form data - ALWAYS prioritize localStorage data over API data
	if (savedFormData) {
		try {
			const parsedData = JSON.parse(savedFormData);
			if (parsedData) {
				initialFormValues = {
					...initialFormValues,
					...parsedData,
					crop: parsedData.crop || null,
					package_type: (parsedData.package_type as 'Standard' | 'Premium' | null) || 'Standard',
					crop_unit: parsedData.crop_unit || { name: t('unitTypes.hectares'), id: 'hectares' },
					communication_language: Array.isArray(parsedData.communication_language) ? 
						parsedData.communication_language : []
				};
				
				// Log that we loaded data from localStorage
				console.log('Loaded form data from localStorage');
			}
		} catch (error) {
			console.error('Error loading form data from localStorage:', error);
		}
	} else if (storedData) {
		// Only use API data if we don't have localStorage data
		console.log('No localStorage data found, using API data');
	}

	// Save form data to localStorage
	const saveFormData = (values: PackagesFormValues) => {
		if (!values) return;
		try {
			localStorage.setItem('monitoringPackagesFormData', JSON.stringify(values));
			console.log('Saved form data to localStorage');
		} catch (error) {
			console.error('Error saving form data to localStorage:', error);
		}
	};

	// Clear saved form data
	const clearSavedFormData = () => {
		try {
			localStorage.removeItem('monitoringPackagesFormData');
			console.log('Cleared form data from localStorage');
		} catch (error) {
			console.error('Error clearing form data from localStorage:', error);
		}
	};

	const formik = useFormik<PackagesFormValues>({
		initialValues: initialFormValues,
		validationSchema,
		enableReinitialize: true,
		validateOnBlur: false,
		validateOnChange: true,
		onSubmit: async (values, { setTouched }) => {
			if (isNavigating) return;
			setIsNavigating(true);
			
			// Clear touched states to prevent validation errors from showing during navigation
			setTouched({});
			
			// Save form data before submitting
			saveFormData(values);
			
			if (customSubmitHandler) {
				// Use the custom handler if provided (for v2)
				try {
					await customSubmitHandler(values);
					setIsNavigating(false);
				} catch (error) {
					setIsNavigating(false);
				}
				// Don't navigate here - let the custom handler handle navigation
			} else {
				// Default submit behavior (for v1)
				setStepsDraft({ packages: values });
				setSubmitIsLoading(true);
				try {
					const response = await submitInitialStep({
						specialist_id: specialists?.data
							? specialists.data.map((specialist: any) => specialist.user_id)
							: [],
						package_type: values.package_type,
						price: values.crop?.fee,
						crop_id: values.crop?.id,
						area: values.crop_area.toString(),
						unit: values.crop_unit?.id,
						location_name: values.crop_location_name,
						latitude: values.latitude.toString(),
						longitude: values.longitude.toString(),
						communication_language: values.communication_language.map((lang: any) => lang.id),
					});

					localStorage.setItem('initialStepDataId', response?.data?.id);

					if (response.success) {
						setSubmitIsLoading(false);
						setInitialStepData(response?.data);

						// Use React Router navigation instead of direct window.location
						setTimeout(() => {
							navigateWithLanguage('/farmer/services/monitoring/terms');
							setIsNavigating(false);
						}, 100);
					} else {
						showSnackbar('Error', 'Error submitting initial step', 'error');
						setSubmitIsLoading(false);
						setIsNavigating(false);
					}
				} catch (error) {
					showSnackbar('Error', 'Error submitting initial step', 'error');
					setSubmitIsLoading(false);
					setIsNavigating(false);
				}
			}
		},
	});

	const { values, setFieldValue, handleSubmit, touched, errors, isValid } = formik;

	// Save form values whenever they change
	useEffect(() => {
		// Don't save empty/initial values
		if (values && (values.crop || values.crop_area || (values.communication_language && values.communication_language.length > 0))) {
			// Convert current values to string for comparison
			const currentValuesString = JSON.stringify(values);
			
			// Only save if values have actually changed
			if (currentValuesString !== previousValuesRef.current) {
				previousValuesRef.current = currentValuesString;
				saveFormData(values);
			}
		}
	}, [values]);

	// Only clear data when unmounting and leaving the monitoring flow completely
	useEffect(() => {
		// Clean up on unmount ONLY if navigating away from monitoring flow completely
		return () => {
			const currentPath = window.location.pathname;
			if (!currentPath.includes('/farmer/services/monitoring/')) {
				clearSavedFormData();
				console.log('Exiting monitoring flow, cleared form data');
			}
		};
	}, []);

	const { specialists, isLoading } = useGetUserByCropsAndLanguages(
		values?.crop?.id,
		values?.communication_language?.map((language: any) => language.id) || [],
		true
	);

	const handleLocationSelect = useCallback(
		(address: string, lat: number, lng: number) => {
			setFieldValue('crop_location_name', address);
			setFieldValue('latitude', lat);
			setFieldValue('longitude', lng);
		},
		[setFieldValue]
	);

	const handlePreviousStep = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		
		if (isNavigating) return;
		setIsNavigating(true);
		
		// When going back to services page, clear the saved form data
		clearSavedFormData();
		
		// Use React Router navigation instead of direct window.location
		setTimeout(() => {
			navigateWithLanguage('/farmer/services');
			setIsNavigating(false);
		}, 100);
	}, [isNavigating, navigateWithLanguage]);

	return (
		<form
			onSubmit={handleSubmit}
			className="flex flex-col xs:mt-0 h-full gap-[20px] p-[20px] relative justify-between"
		>
			<div className="flex items-center h-full justify-center sm:mt-0 mt-[20px]">
				<div className="flex flex-wrap flex-col max-w-[500px] w-full gap-[20px]">
					<h1 className="text-dark text-center font-bold text-xl leading-normal mb-[20px] mt-[50px]">
						<SearchOutlinedIcon /> {t('findSpecialistPage.monitoring.chooseYourPackages')}
					</h1>

					<RoleSelector
						selectedRole={values.package_type}
						onSelect={(type) => setFieldValue('package_type', type)}
						roles={rolesData}
					/>

					<SafeAutocompleteField
						formik={formik}
						name="crop"
						label={t('selectCrop')}
						multiple={false}
						options={cropsMonitoring}
						icon={<SpaOutlinedIcon color={touched.crop && errors.crop && !isNavigating ? 'error' : 'primary'} />}
					/>

					<SafeCropAreaAndUnit formik={formik} />

					<LocationInput formik={formik} loading={loading} openMapsModal={() => setMapOpen(true)} />

					<SafeAutocompleteField
						formik={formik}
						name="communication_language"
						label={t('communicationLanguage')}
						options={languages}
						multiple
						icon={
							<RecordVoiceOverOutlinedIcon
								color={
									touched.communication_language && errors.communication_language && !isNavigating
										? 'error'
										: 'primary'
								}
							/>
						}
					/>
					<Button
						disabled={!isValid || isNavigating || submitIsLoading}
						fullWidth
						type="submit"
						variant="contained"
						sx={{ marginTop: 2 }}
						endIcon={<Icon path={mdiChevronRight} size={1} />}
					>
						{(isNavigating || submitIsLoading) ? (
							<CircularProgress size={24} color="inherit" />
						) : (
							t('next')
						)}
					</Button>
				</div>
			</div>

			<Button
				className="w-[120px]"
				type="button"
				onClick={handlePreviousStep}
				variant="contained"
				sx={{ backgroundColor: 'white', color: 'black' }}
				startIcon={<Icon path={mdiChevronLeft} size={1} />}
				disabled={isNavigating}
			>
				{isNavigating ? (
					<CircularProgress size={24} color="inherit" />
				) : (
					t('previous')
				)}
			</Button>

			<GeneralMuiModal
				maxWidth="xl"
				fullScreenOnMobile
				title={t('myFarm.farmLocation')}
				styles={{
					paperStyle: {
						width: modalDimensions.width,
						height: modalDimensions.height,
						padding: 0,
					},
					contentStyle: { padding: 0 },
				}}
				open={mapOpen}
				onClose={() => setMapOpen(false)}
				content={
					<TaMaps
						onSave={() => setMapOpen(false)}
						currentAddress={values.crop_location_name}
						marker={{
							lat: Number(values.latitude),
							lng: Number(values.longitude),
						}}
						onLocationSelect={handleLocationSelect}
					/>
				}
			/>
			<Backdrop sx={backdropStyle} open={isLoading && !isNavigating}>
				<CircularProgress color="primary" />
			</Backdrop>
		</form>
	);
};

export const PackagesStep = () => {
	return <PackagesStepComponent />;
};
